import React, { useContext, useEffect, useState } from 'react';
import { ConversationAction } from '../../../../enums/ConversationAction';
import { ConversationThreadSchema } from '../../../../definitions/schemas';
import { DEFAULT_ROOM_MESSAGE_ID } from '../../../Conversation/Message';
import { Events, Properties, Referrers } from '../../../../enums/Analytics';
import { FullScreenTrackingContext } from '../../../Rooms/Tracking/FullScreenTrackingContext';
import { QUERY_STRING } from '../../../../enums/QueryString';
import Analytics from '../../../../tools/Analytics';
import AnonymousIdentityGuard from '../../View/AnonymousIdentityGuard';
import ConversationButton from '../../../Rooms/Buttons/ConversationButton';
import RoomConversationDialog, {
  RoomConversationDialogProps,
} from '../Conversation/RoomConversationDialog';
import UserRolesStore from '../../../../stores/observables/UserRolesStore';

type ConversationProps = Omit<RoomConversationDialogProps, 'onClose' | 'open'>;

interface Props extends ConversationProps {
  disabled?: boolean;
  initialComposerMessage?: string | null;
  isQuickAccessIcon?: boolean;
  initialOpen?: boolean;
  isMenuItem?: boolean;
  messagesCount: number;
  onClose?: () => void;
  onConversationDialogOpened?: () => void;
  onConversationDialogClosed?: () => void;
  onConversationMessageConsumed?: () => void;
  referrer: string;
}

const RoomConversationButton: React.FC<Props> = (props: Props): JSX.Element => {
  const { fullScreenContentId, setFullScreenContentId } = useContext(
    FullScreenTrackingContext
  );

  const {
    disabled,
    initialComposerMessage,
    isQuickAccessIcon = false,
    initialOpen = false,
    isMenuItem = false,
    messagesCount: initialMessagesCount,
    onClose,
    onConversationDialogOpened,
    onConversationDialogClosed,
    onConversationMessageConsumed,
    referrer,
    ...RoomConversationDialogProps
  } = props;

  const { roomMessageId: initialRoomMessageId } = RoomConversationDialogProps;

  const [open, setOpen] = useState(false);
  const [messagesCount, setMessagesCount] = useState(initialMessagesCount);
  const [roomMessageId, setRoomMessageId] = useState(initialRoomMessageId);
  const [localInitialMessage, setLocalInitialMessage] = useState<string | null>(
    null
  );

  const currentRole = UserRolesStore.getCurrent();

  useEffect(() => {
    if (initialComposerMessage) {
      setOpen(true);
      setLocalInitialMessage(initialComposerMessage);
      onConversationMessageConsumed?.();
      onConversationDialogOpened?.();
    }
  }, [
    initialComposerMessage,
    onConversationMessageConsumed,
    onConversationDialogOpened,
  ]);

  useEffect(() => {
    // clear the value to avoid highlight on the re-render
    setTimeout(() => {
      setRoomMessageId('');
    }, 1000);

    if (!(initialRoomMessageId || initialOpen) || fullScreenContentId) {
      return;
    }

    trackingConversationDrawerOpened(Referrers.DEAL_ROOMS.EMAIL);

    setFullScreenContentId(`conversation-message-${initialRoomMessageId}`);
    setOpen(true);
    onConversationDialogOpened?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const trackingConversationDrawerOpened = (referrer: string) => {
    if (!currentRole) {
      return;
    }
    Analytics.track(Events.CONVERSATION_DRAWER_OPENED, {
      [Properties.ENTERED_FROM]: referrer,
    });
  };

  const onOpen = () => {
    trackingConversationDrawerOpened(referrer);
    setOpen(true);
    onConversationDialogOpened?.();
  };

  const handleClose = () => {
    setOpen(false);
    setLocalInitialMessage(null);
    setFullScreenContentId(null);
    if (onClose) {
      onClose();
    }
    if (onConversationDialogClosed) {
      onConversationDialogClosed();
    }
  };

  const onConversationUpdated = (
    conversation: ConversationThreadSchema,
    action: ConversationAction,
    deletedAmount = 0
  ) => {
    switch (action) {
      case ConversationAction.Create:
        setMessagesCount(messagesCount + 1);
        break;
      case ConversationAction.Delete:
        setMessagesCount(messagesCount - deletedAmount);
        break;
    }
  };

  const renderButton = () => {
    const button = (
      <ConversationButton
        disableBreakPoint={isMenuItem}
        disabled={disabled}
        fullWidth={isMenuItem}
        isQuickAccessIcon={isQuickAccessIcon}
        messagesCount={messagesCount}
        onClick={onOpen}
        variant={isMenuItem ? 'outlined' : undefined}
      />
    );

    // If there are 0 conversation messages, you’re nonymously and mentions is enabled,
    // when you click “Start a conversation”, prompt for email
    if (!messagesCount) {
      return (
        <AnonymousIdentityGuard
          queryParams={{
            [QUERY_STRING.ROOM_MESSAGE_ID]: DEFAULT_ROOM_MESSAGE_ID,
          }}
          referrer={Referrers.DEAL_ROOMS.ACTIVITY.MESSAGE_INPUT}
        >
          {button}
        </AnonymousIdentityGuard>
      );
    }

    return button;
  };

  return (
    <>
      {renderButton()}
      <RoomConversationDialog
        {...RoomConversationDialogProps}
        initialComposerMessage={localInitialMessage}
        onClose={handleClose}
        onConversationUpdated={onConversationUpdated}
        open={open}
        roomMessageId={roomMessageId}
      />
    </>
  );
};

export default RoomConversationButton;
